import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

export interface ServiceLogEntry {
  id: string;
  org_id: string;
  asset_id: string;
  note: string;
  performed_by: string;
  performed_at: string;
  created_at: string;
}

export function useAssetServiceLog(assetId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["asset-service-log", assetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_service_log" as any)
        .select("*")
        .eq("asset_id", assetId!)
        .order("performed_at", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as ServiceLogEntry[];
    },
    enabled: !!assetId && isAuthenticated && !authLoading,
  });
}

export function useAddServiceLogEntry() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      assetId,
      note,
      performedBy,
      performedAt,
    }: {
      assetId: string;
      note: string;
      performedBy: string;
      performedAt: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: ua } = await supabase
        .from("user_account" as any)
        .select("org_id")
        .eq("external_auth_user", user!.id)
        .single();
      const { data, error } = await supabase
        .from("asset_service_log" as any)
        .insert({
          org_id: (ua as any).org_id,
          asset_id: assetId,
          note,
          performed_by: performedBy,
          performed_at: performedAt,
        })
        .select()
        .single();
      if (error) throw error;
      return data as ServiceLogEntry;
    },
    onSuccess: (_data, { assetId }) => {
      qc.invalidateQueries({ queryKey: ["asset-service-log", assetId] });
      toast({ title: "Entry added", description: "Service log entry saved." });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function useDeleteServiceLogEntry() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, assetId }: { id: string; assetId: string }) => {
      // Remove storage files before cascade-deleting the entry row
      const { data: files } = await supabase
        .from("asset_file" as any)
        .select("storage_path")
        .eq("service_log_id", id);

      if (files && (files as any[]).length > 0) {
        const paths = (files as any[]).map((f) => f.storage_path as string);
        await supabase.storage.from("asset-files").remove(paths);
      }

      const { error } = await supabase
        .from("asset_service_log" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
      return assetId;
    },
    onSuccess: (assetId) => {
      qc.invalidateQueries({ queryKey: ["asset-service-log", assetId] });
      qc.invalidateQueries({ queryKey: ["service-log-files", assetId] });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}
